import { UiButtonDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
  WorkflowDefinitionDraftComparison,
  WorkflowDefinitionDraftDetail,
  WorkflowDefinitionSemanticChange,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import {
  WorkflowRegistryDiagnosticDisplayError,
  mapWorkflowRegistryDiagnosticError,
} from './platform-workflow-registry-diagnostics.models';

interface SavedDraftKey {
  draftId: number;
  revision: number;
  eTag: string;
  contentHash: string;
}

type StaleReason =
  | 'local_changes'
  | 'saved_revision'
  | 'process_settings'
  | 'refresh_failed';

type SemanticChangeGroupKey =
  | 'setup'
  | 'assignment'
  | 'steps'
  | 'behavior'
  | 'staff_pages'
  | 'dashboards'
  | 'paths'
  | 'state_changes'
  | 'decisions'
  | 'other';

interface SemanticChangeGroup {
  key: SemanticChangeGroupKey;
  label: string;
  description: string;
  changes: WorkflowDefinitionSemanticChange[];
}

interface SemanticChangeTypeCounts {
  added: number;
  changed: number;
  removed: number;
}

interface ReadableChangeValue {
  kind: 'absent' | 'empty' | 'plain' | 'structured';
  text: string;
  facts: Array<{ label: string; value: string }>;
}

const CHANGE_GROUPS: ReadonlyArray<Omit<SemanticChangeGroup, 'changes'>> = [
  {
    key: 'setup',
    label: 'Workflow setup',
    description: 'Version, subject, scope, and configuration',
  },
  {
    key: 'assignment',
    label: 'Work responsibility',
    description: 'Who receives the work and how it is assigned',
  },
  {
    key: 'behavior',
    label: 'Step behavior',
    description: 'Completion requirements and staff actions',
  },
  {
    key: 'staff_pages',
    label: 'Staff pages',
    description: 'Task forms, instructions and supporting content',
  },
  {
    key: 'dashboards',
    label: 'Dashboards',
    description: 'Included work, visibility, tiles, filters and columns',
  },
  {
    key: 'steps',
    label: 'Work steps',
    description: 'The work people move through',
  },
  {
    key: 'paths',
    label: 'Paths',
    description: 'How work moves between steps',
  },
  {
    key: 'state_changes',
    label: 'Status changes',
    description: 'Allowed workflow and step status changes',
  },
  {
    key: 'decisions',
    label: 'Decision rules',
    description: 'Outcomes, reactions, roles, and queues',
  },
  {
    key: 'other',
    label: 'Other changes',
    description: 'Changes not mapped to a work area',
  },
];

@Component({
  selector: 'phms-platform-workflow-draft-semantic-diff',
  imports: [UiButtonDirective, CommonModule],
  templateUrl: './platform-workflow-draft-semantic-diff.component.html',
  styleUrl: './platform-workflow-draft-semantic-diff.component.scss',
})
export class PlatformWorkflowDraftSemanticDiffComponent
  implements OnChanges, OnDestroy
{
  private readonly lifecycleService = inject(
    PlatformWorkflowDefinitionLifecycleService,
  );
  private comparisonSubscription?: Subscription;
  private comparisonRequestId = 0;
  private readonly activeDraftKey = signal<SavedDraftKey | null>(null);
  private readonly localChangesPendingState = signal(false);
  private readonly retainedComparisonUnverified = signal(false);
  private readonly activeState = signal(false);

  @ViewChild('comparisonError')
  private comparisonError?: ElementRef<HTMLElement>;

  @ViewChild('changeInspectorHeading')
  private changeInspectorHeading?: ElementRef<HTMLElement>;

  @Input({ required: true }) draft: WorkflowDefinitionDraftDetail | null = null;
  @Input() localChangesPending = false;
  @Input() active = false;
  @Input() interactionBlocked = false;

  @Output() readonly changeFocusRequested =
    new EventEmitter<WorkflowDefinitionSemanticChange>();

  readonly isLoading = signal(false);
  readonly comparison = signal<WorkflowDefinitionDraftComparison | null>(null);
  readonly error = signal<WorkflowRegistryDiagnosticDisplayError | null>(null);
  readonly loadedDraftKey = signal<SavedDraftKey | null>(null);
  readonly selectedGroupKey = signal<SemanticChangeGroupKey | null>(null);
  readonly selectedChangeKey = signal<string | null>(null);

  readonly staleReason = computed<StaleReason | null>(() => {
    if (this.comparison() === null) {
      return null;
    }

    const loaded = this.loadedDraftKey();
    const active = this.activeDraftKey();
    if (this.localChangesPendingState()) {
      return 'local_changes';
    }
    if (!loaded || !active || !sameSavedDraft(loaded, active)) {
      if (
        loaded &&
        active &&
        loaded.draftId === active.draftId &&
        loaded.revision === active.revision &&
        loaded.eTag === active.eTag
      ) {
        return 'process_settings';
      }
      return 'saved_revision';
    }
    return this.retainedComparisonUnverified() ? 'refresh_failed' : null;
  });
  readonly isStale = computed(() => this.staleReason() !== null);
  readonly editorHandoffUnavailable = computed(() => {
    const loaded = this.loadedDraftKey();
    const active = this.activeDraftKey();
    return !loaded || !active || !sameSavedDraft(loaded, active);
  });

  readonly baselineLabel = computed(() => {
    const comparison = this.comparison();
    if (comparison?.baselineDefinitionVersionCode) {
      return `Version ${comparison.baselineDefinitionVersionCode}`;
    }

    if (comparison?.definitionVersionId) {
      return `Version ${comparison.definitionVersionId}`;
    }

    return 'No published baseline';
  });

  readonly hasPublishedBaseline = computed(() => {
    const result = this.comparison();
    return (
      result !== null &&
      (result.definitionVersionId !== null ||
        !!result.baselineDefinitionVersionCode)
    );
  });

  readonly changeTypeCounts = computed<SemanticChangeTypeCounts>(() => {
    const counts: SemanticChangeTypeCounts = {
      added: 0,
      changed: 0,
      removed: 0,
    };
    for (const change of this.comparison()?.changes ?? []) {
      if (change.changeTypeCode === 'added') {
        counts.added++;
      } else if (change.changeTypeCode === 'removed') {
        counts.removed++;
      } else {
        counts.changed++;
      }
    }
    return counts;
  });

  readonly changeGroups = computed<SemanticChangeGroup[]>(() =>
    CHANGE_GROUPS.map((group) => ({
      ...group,
      changes: (this.comparison()?.changes ?? []).filter(
        (change) => groupKeyForChange(change) === group.key,
      ),
    })).filter((group) => group.changes.length > 0),
  );

  readonly selectedGroup = computed(() => {
    const groups = this.changeGroups();
    return (
      groups.find((group) => group.key === this.selectedGroupKey()) ??
      groups[0] ??
      null
    );
  });

  readonly selectedChange = computed(() => {
    const group = this.selectedGroup();
    if (!group) {
      return null;
    }

    return (
      group.changes.find(
        (change) => this.changeKey(change) === this.selectedChangeKey(),
      ) ??
      group.changes[0] ??
      null
    );
  });

  readonly selectedValues = computed(() => {
    const change = this.selectedChange();
    return change
      ? {
          before: readableChangeValue(change, change.beforeValue),
          after: readableChangeValue(change, change.afterValue),
        }
      : null;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['localChangesPending']) {
      this.localChangesPendingState.set(this.localChangesPending);
    }

    const wasActive = this.activeState();
    if (changes['active']) {
      this.activeState.set(this.active);
    }

    let savedDraftChanged = false;
    if (changes['draft']) {
      const previousDraftId = this.activeDraftKey()?.draftId ?? null;
      const nextKey = toSavedDraftKey(this.draft);
      if (!sameOptionalSavedDraft(this.activeDraftKey(), nextKey)) {
        savedDraftChanged = true;
        this.activeDraftKey.set(nextKey);
        this.cancelRequest();
        this.isLoading.set(false);
        this.error.set(null);

        if (previousDraftId !== nextKey?.draftId) {
          this.comparison.set(null);
          this.loadedDraftKey.set(null);
          this.retainedComparisonUnverified.set(false);
          this.selectedGroupKey.set(null);
          this.selectedChangeKey.set(null);
        }
      }
    }

    if (!this.activeState()) {
      if (wasActive) {
        this.cancelRequest();
        this.isLoading.set(false);
      }
      return;
    }

    const nextKey = this.activeDraftKey();
    if (!nextKey) {
      this.cancelRequest();
      this.isLoading.set(false);
      this.error.set(null);
      this.retainedComparisonUnverified.set(false);
      return;
    }

    const loadedKey = this.loadedDraftKey();
    const currentResultIsVerified =
      this.comparison() !== null &&
      loadedKey !== null &&
      sameSavedDraft(loadedKey, nextKey) &&
      !this.retainedComparisonUnverified() &&
      this.error() === null;
    if (savedDraftChanged || (!wasActive && !currentResultIsVerified)) {
      this.loadComparison();
    }
  }

  ngOnDestroy(): void {
    this.comparisonRequestId++;
    this.comparisonSubscription?.unsubscribe();
  }

  retry(): void {
    if (!this.activeState() || !this.activeDraftKey() || this.isLoading()) {
      return;
    }

    this.loadComparison();
  }

  displayCode(value: string): string {
    return value
      .split(/[_-]/)
      .filter((segment) => segment.length > 0)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  changeKey(change: WorkflowDefinitionSemanticChange): string {
    return semanticChangeKey(change);
  }

  trackGroup(
    _index: number,
    group: SemanticChangeGroup,
  ): SemanticChangeGroupKey {
    return group.key;
  }

  trackChange(
    _index: number,
    change: WorkflowDefinitionSemanticChange,
  ): string {
    return semanticChangeKey(change);
  }

  selectGroup(group: SemanticChangeGroup): void {
    this.selectedGroupKey.set(group.key);
    this.selectedChangeKey.set(this.changeKey(group.changes[0]));
  }

  selectChange(
    change: WorkflowDefinitionSemanticChange,
    focusInspector = false,
  ): void {
    this.selectedChangeKey.set(this.changeKey(change));
    if (focusInspector) {
      setTimeout(() => this.changeInspectorHeading?.nativeElement.focus());
    }
  }

  isSelectedChange(change: WorkflowDefinitionSemanticChange): boolean {
    return this.selectedChangeKey() === this.changeKey(change);
  }

  focusChange(change: WorkflowDefinitionSemanticChange): void {
    if (
      this.interactionBlocked ||
      this.editorHandoffUnavailable() ||
      this.isPlatformSettingsChange(change)
    ) {
      return;
    }
    this.changeFocusRequested.emit(change);
  }

  isPlatformSettingsChange(change: WorkflowDefinitionSemanticChange): boolean {
    return (
      change.elementTypeCode === 'definition' &&
      (change.path === '$.configurationCode' ||
        change.path === '$.configurationVersionCode')
    );
  }

  changeAreaLabel(change: WorkflowDefinitionSemanticChange): string {
    return (
      CHANGE_GROUPS.find((group) => group.key === groupKeyForChange(change))
        ?.label ?? 'Workflow editor'
    );
  }

  changeTitle(change: WorkflowDefinitionSemanticChange): string {
    const verb = this.displayCode(change.changeTypeCode).toLowerCase();
    if (change.path.includes('statusConfiguration'))
      return `${change.path.startsWith('$.nodes[') ? 'Step' : 'Workflow'} statuses ${verb}`;
    if (this.isPlatformSettingsChange(change)) {
      return `${change.path === '$.configurationVersionCode' ? 'Process settings version' : 'Process settings'} ${verb}`;
    }
    if (
      change.path.startsWith('$.nodes[') &&
      change.path.includes('.notificationMessage')
    ) {
      const field = change.path.split('.').at(-1);
      const label =
        field === 'titleTemplate'
          ? 'Notification title'
          : field === 'bodyTemplate'
            ? 'Notification body'
            : field === 'contextProfileCode' ||
                field === 'contextProfileVersion'
              ? 'Notification context'
              : 'Notification message';
      return `${label} ${verb}`;
    }
    const group = groupKeyForChange(change);
    if (group === 'behavior') return `Step behavior ${verb}`;
    if (group === 'staff_pages') return `Staff page ${verb}`;
    if (group === 'dashboards')
      return `Dashboard${change.path.endsWith('.order') ? ' order' : ''} ${verb}`;
    if (change.elementTypeCode === 'node') {
      const field = change.path.split('.').at(-1);
      const labels: Record<string, string> = {
        displayName: 'Step name',
        stepCode: 'Step code',
        displayOrder: 'Step order',
        typeCode: 'Step type',
        conditionCode: 'Routing condition',
        conditionVersion: 'Routing condition version',
      };
      return `${labels[field ?? ''] ?? 'Workflow step'} ${verb}`;
    }
    const labels: Record<string, string> = {
      edge: 'Path',
      assignment_policy: 'Work responsibility',
      workflow_transition: 'Workflow status rule',
      step_transition: 'Step status rule',
      decision: 'Decision rule',
      notification: 'Notification',
      action_trigger: 'Action trigger',
      action: 'Triggered action',
    };
    return labels[change.elementTypeCode]
      ? `${labels[change.elementTypeCode]} ${verb}`
      : change.summary;
  }

  changeLocation(change: WorkflowDefinitionSemanticChange): string {
    const nodeId =
      /^\$\.nodes\[id=([^\]]+)\]/.exec(change.path)?.[1] ??
      (change.elementTypeCode === 'node' ? change.elementId : null);
    const exactSavedDocument = sameOptionalSavedDraft(
      this.loadedDraftKey(),
      this.activeDraftKey(),
    )
      ? this.draft?.document
      : null;
    if (nodeId) {
      const matches =
        exactSavedDocument?.nodes.filter((node) => node?.id === nodeId) ?? [];
      return matches.length === 1
        ? matches[0].displayName || matches[0].stepCode || nodeId
        : `Step · ${nodeId}`;
    }
    if (
      change.elementTypeCode === 'definition' ||
      change.elementTypeCode === 'workflow_transition'
    )
      return 'Whole workflow';
    if (change.path.startsWith('$.requiredDecisions[')) return 'Whole workflow';
    if (change.elementTypeCode === 'edge')
      return `Path · ${change.elementId || 'Identity unavailable'}`;
    if (change.elementTypeCode === 'assignment_policy')
      return `Responsibility · ${change.elementId || 'Identity unavailable'}`;
    return change.elementId || this.changeAreaLabel(change);
  }

  editorActionLabel(change: WorkflowDefinitionSemanticChange): string {
    if (change.path.includes('statusConfiguration'))
      return 'Open custom statuses';
    if (
      change.path.startsWith('$.nodes[') &&
      change.path.includes('.notificationMessage')
    )
      return 'Open notification message';
    switch (groupKeyForChange(change)) {
      case 'setup':
        return 'Open Workflow setup';
      case 'assignment':
        return 'Open Work responsibility';
      case 'steps':
      case 'behavior':
      case 'staff_pages':
      case 'dashboards':
        return 'Open Designer';
      case 'paths':
        return 'Open Paths';
      case 'state_changes':
        return 'Open Status changes';
      case 'decisions':
        return 'Open Decisions';
      default:
        return 'Open workflow editor';
    }
  }

  private loadComparison(): void {
    const requestKey = this.activeDraftKey();
    if (!requestKey) {
      return;
    }

    this.cancelRequest();
    const requestId = ++this.comparisonRequestId;
    this.isLoading.set(true);
    this.error.set(null);
    this.comparisonSubscription = this.lifecycleService
      .compareDraft(requestKey.draftId)
      .subscribe({
        next: (comparison) => {
          if (
            requestId !== this.comparisonRequestId ||
            !sameOptionalSavedDraft(this.activeDraftKey(), requestKey)
          ) {
            return;
          }

          if (!comparisonMatches(comparison, requestKey)) {
            this.isLoading.set(false);
            this.retainedComparisonUnverified.set(this.comparison() !== null);
            this.error.set({
              title: 'Draft comparison changed',
              messages: [
                'The saved draft changed, or its process settings were updated while its review was loading. Reload the draft before retrying the comparison.',
              ],
            });
            this.focusError();
            return;
          }

          this.comparison.set(comparison);
          this.loadedDraftKey.set(requestKey);
          this.isLoading.set(false);
          this.error.set(null);
          this.retainedComparisonUnverified.set(false);
          this.synchronizeSelection(comparison.changes);
        },
        error: (error: unknown) => {
          if (
            requestId !== this.comparisonRequestId ||
            !sameOptionalSavedDraft(this.activeDraftKey(), requestKey)
          ) {
            return;
          }

          this.isLoading.set(false);
          this.retainedComparisonUnverified.set(this.comparison() !== null);
          this.error.set(mapWorkflowRegistryDiagnosticError(error));
          this.focusError();
        },
      });
  }

  private cancelRequest(): void {
    this.comparisonSubscription?.unsubscribe();
    this.comparisonSubscription = undefined;
  }

  private focusError(): void {
    setTimeout(() => {
      if (this.activeState()) {
        this.comparisonError?.nativeElement.focus();
      }
    });
  }

  private synchronizeSelection(
    changes: WorkflowDefinitionSemanticChange[],
  ): void {
    if (changes.length === 0) {
      this.selectedGroupKey.set(null);
      this.selectedChangeKey.set(null);
      return;
    }

    const existingChangeKey = this.selectedChangeKey();
    const selected = existingChangeKey
      ? changes.find((change) => this.changeKey(change) === existingChangeKey)
      : null;
    const nextChange = selected ?? changes[0];
    this.selectedGroupKey.set(groupKeyForChange(nextChange));
    this.selectedChangeKey.set(this.changeKey(nextChange));
  }
}

function groupKeyForChange(
  change: WorkflowDefinitionSemanticChange,
): SemanticChangeGroupKey {
  if (change.path.includes('statusConfiguration')) return 'state_changes';
  switch (change.elementTypeCode) {
    case 'definition':
      return 'setup';
    case 'assignment_policy':
      return 'assignment';
    case 'node':
      if (/\.declaration\.behavior(?:\.|$)/.test(change.path))
        return 'behavior';
      if (/\.declaration\.detail(?:\.|$)/.test(change.path))
        return 'staff_pages';
      if (/\.declaration\.dashboards(?:\[|\.|$)/.test(change.path))
        return 'dashboards';
      return 'steps';
    case 'edge':
      return 'paths';
    case 'workflow_transition':
    case 'step_transition':
      return 'state_changes';
    case 'decision':
      return 'decisions';
    case 'notification':
      return change.path.includes('.requiredDecisions[')
        ? 'decisions'
        : 'state_changes';
    default:
      return 'other';
  }
}

function semanticChangeKey(change: WorkflowDefinitionSemanticChange): string {
  return [
    change.changeTypeCode,
    change.elementTypeCode,
    change.elementId,
    change.path,
    change.summary,
  ].join(':');
}

function readableChangeValue(
  change: WorkflowDefinitionSemanticChange,
  value: string | null,
): ReadableChangeValue {
  if (value === null) return { kind: 'absent', text: 'Not present', facts: [] };
  if (value === '') return { kind: 'empty', text: 'Empty value', facts: [] };
  const structured = (
    text: string,
    facts: ReadableChangeValue['facts'] = [],
  ): ReadableChangeValue => ({
    kind: 'structured',
    text,
    facts,
  });
  if (value.length > 256_000)
    return structured('Configuration details available below');
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    if (value.trimStart().startsWith('{') || value.trimStart().startsWith('['))
      return structured('Configuration could not be summarized');
    const descriptions: Record<string, Record<string, string>> = {
      node: {
        type: 'Type',
        order: 'Order',
        step: 'Step code',
        name: 'Name',
        routing: 'Routing condition',
      },
      assignment_policy: {
        scope: 'Scope',
        step: 'Work step',
        'initial queue': 'Receiving queue',
        'eligible queues': 'Eligible queues',
        'eligible roles': 'Eligible roles',
        mode: 'Assignment mode',
        unassigned: 'Unassigned work',
        fallback: 'Fallback',
        return: 'Return visit',
        're-entry': 'Re-entry',
      },
      decision: {
        kind: 'Decision kind',
        outcome: 'Required outcome',
        reaction: 'Reaction',
        target: 'Target status',
        roles: 'Eligible roles',
        queue: 'Owner queue',
      },
      notification: {
        recipient: 'Recipient',
        target: 'Target',
        urgency: 'Urgency',
      },
    };
    const labels = descriptions[change.elementTypeCode];
    const entries = value
      .split('; ')
      .map((part) => /^([^=]+)=(.*)$/.exec(part));
    if (labels && entries.every((entry) => entry && labels[entry[1]]))
      return structured(
        'Settings',
        entries.map((entry) => ({
          label: labels[entry![1]],
          value: readableCode(entry![2], entry![1]),
        })),
      );
    return { kind: 'plain', text: value, facts: [] };
  }
  if (parsed === null) return structured('No configuration');
  if (typeof parsed === 'string')
    return {
      kind: parsed === '' ? 'empty' : 'plain',
      text: parsed || 'Empty value',
      facts: [],
    };
  if (typeof parsed !== 'object')
    return { kind: 'plain', text: String(parsed), facts: [] };
  if (Array.isArray(parsed)) {
    const label =
      change.path === '$.capabilityReferences'
        ? 'module dependency entries'
        : change.path.endsWith('.dashboards.order')
          ? 'dashboards in order'
          : 'configuration entries';
    return structured(`${parsed.length} ${label}`);
  }
  const record = parsed as Record<string, unknown>;
  const facts: ReadableChangeValue['facts'] = [];
  if (change.path.includes('statusConfiguration')) {
    const statuses = jsonField(record, 'statuses');
    const rules = jsonField(record, 'rules');
    const initial = jsonField(record, 'initialStatusCode');
    if (Array.isArray(statuses)) {
      const names = statuses
        .filter(
          (s): s is Record<string, unknown> => !!s && typeof s === 'object',
        )
        .map((s) => jsonField(s, 'label'))
        .filter((label): label is string => typeof label === 'string');
      facts.push({ label: 'Statuses', value: names.join(', ') || 'None' });
      const starting = statuses.find(
        (s) =>
          s &&
          typeof s === 'object' &&
          jsonField(s as Record<string, unknown>, 'code') === initial,
      );
      if (starting)
        facts.push({
          label: 'Starting status',
          value: String(
            jsonField(starting as Record<string, unknown>, 'label') ||
              'Unavailable',
          ),
        });
    }
    if (Array.isArray(rules))
      facts.push({ label: 'Automatic changes', value: String(rules.length) });
    facts.push({
      label: 'Staff changes',
      value:
        jsonField(record, 'allowManualChanges') === true
          ? 'Allowed'
          : 'Automatic only',
    });
    return structured('Custom statuses', facts);
  }
  const count = (field: string, label: string) => {
    const items = jsonField(record, field);
    if (Array.isArray(items))
      facts.push({ label, value: String(items.length) });
  };
  const text = (
    field: string,
    label: string,
    values: Record<string, string> = {},
  ) => {
    const item = jsonField(record, field);
    if (typeof item === 'string' || typeof item === 'number')
      facts.push({ label, value: values[String(item)] ?? String(item) });
  };
  if (/\.declaration\.behavior(?:\.|$)/.test(change.path)) {
    count('commands', 'Staff actions');
    count('completionRequirements', 'Completion requirements');
    text('assignmentPolicyId', 'Responsibility policy');
    return structured('Step behavior', facts);
  }
  if (/\.declaration\.detail(?:\.|$)/.test(change.path)) {
    count('blocks', 'Content blocks');
    text('contextPlacement', 'Supporting content', {
      side: 'Beside the page',
      below: 'Below the page',
      hidden: 'Hidden',
    });
    if (jsonField(record, 'mainInteraction') != null)
      facts.push({ label: 'Task form', value: 'Configured' });
    return structured('Staff page', facts);
  }
  if (/\.declaration\.dashboards\[/.test(change.path)) {
    text('label', 'Name');
    count('columns', 'Columns');
    count('filterFields', 'Staff filters');
    const presentation = jsonField(record, 'presentation');
    const tiles =
      presentation &&
      typeof presentation === 'object' &&
      !Array.isArray(presentation)
        ? jsonField(presentation as Record<string, unknown>, 'tiles')
        : undefined;
    if (Array.isArray(tiles))
      facts.push({ label: 'Summary tiles', value: String(tiles.length) });
    else count('summaries', 'Summary tiles');
    count('audienceRoleCodes', 'Audience roles');
    return structured('Dashboard', facts);
  }
  if (change.path === '$.numbering') {
    text('mode', 'Numbering', { disabled: 'Off', required: 'Required' });
    text('policyId', 'Numbering policy');
    text('agencyCode', 'Agency');
    text('programCode', 'Program');
    return structured('Public reference numbering', facts);
  }
  if (change.path === '$.packReference') {
    text('code', 'Pack');
    text('version', 'Revision');
    return structured('Jurisdiction pack', facts);
  }
  return structured('Configuration details available below');
}

function jsonField(record: Record<string, unknown>, key: string): unknown {
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  // Conflicting spellings are opaque rather than allowing one to hide the other.
  if (Object.hasOwn(record, key) && Object.hasOwn(record, pascalKey))
    return undefined;
  return Object.hasOwn(record, key) ? record[key] : record[pascalKey];
}

function readableCode(value: string, field: string): string {
  if (value === '(none)') return 'None';
  if (
    ![
      'type',
      'scope',
      'mode',
      'unassigned',
      'fallback',
      'return',
      're-entry',
      'recipient',
      'urgency',
    ].includes(field)
  )
    return value;
  const labels: Record<string, string> = {
    '(none)': 'None',
    human_step: 'Work step',
    terminal: 'Complete workflow',
    entry: 'Start point',
    routing_decision: 'Routing condition',
    workflow: 'Whole workflow',
    step: 'This step',
    queue_only: 'Queue only',
    claim: 'Claim',
    direct: 'Direct assignment',
    claim_or_direct: 'Claim or direct assignment',
    require_queue: 'Queue required',
    allow_unassigned: 'Unassigned allowed',
    initial_queue: 'Receiving queue',
    retain_queue: 'Keep queue',
    retain_assignment: 'Keep assignment',
    unassigned: 'Unassigned',
    block: 'Block',
    current_queue: 'Current queue',
    current_assignee: 'Current assignee',
    info: 'Information',
    warning: 'Warning',
    critical: 'Critical',
  };
  return labels[value] ?? value;
}

function toSavedDraftKey(
  draft: WorkflowDefinitionDraftDetail | null,
): SavedDraftKey | null {
  return draft
    ? {
        draftId: draft.id,
        revision: draft.revision,
        eTag: draft.eTag,
        contentHash: draft.canonicalContentHash,
      }
    : null;
}

function comparisonMatches(
  comparison: WorkflowDefinitionDraftComparison,
  expected: SavedDraftKey,
): boolean {
  return (
    comparison.draftId === expected.draftId &&
    comparison.draftRevision === expected.revision &&
    comparison.draftETag === expected.eTag &&
    comparison.draftContentHash === expected.contentHash
  );
}

function sameOptionalSavedDraft(
  left: SavedDraftKey | null,
  right: SavedDraftKey | null,
): boolean {
  if (!left || !right) {
    return left === right;
  }

  return sameSavedDraft(left, right);
}

function sameSavedDraft(left: SavedDraftKey, right: SavedDraftKey): boolean {
  return (
    left.draftId === right.draftId &&
    left.revision === right.revision &&
    left.eTag === right.eTag &&
    left.contentHash === right.contentHash
  );
}
